import * as THREE from "three";

import Experience from "../Experience.js";
import WaterSurface from "./components/environment/WaterSurface.js";
import OceanFloor from "./components/environment/OceanFloor.js";
import Caustics from "./components/environment/Caustics.js";
import Lights from "./components/environment/Lights.js";
import Fog from "./components/environment/Fog.js";
import Bounds from "./components/boids/Bounds.js";
import BoidSimulation from "./components/boids/BoidSimulation.js";
import Plankton from "./components/environment/Plankton.js";
import FloorRocks from "./components/environment/FloorRocks.js";
import Seaweed from "./components/environment/Seaweed.js";

export default class World {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.resources = this.experience.resources;

        // Setup bounds
        this.bounds = new Bounds(120, 40, 120);
        this.fogColor = new THREE.Color("#1e4f9f");

        // Wait for resources
        this.resources.on("ready", () => {
            // Setup environment
            this.lights = new Lights(this.bounds);
            this.fog = new Fog(this.fogColor);
            this.caustics = new Caustics(this.bounds);
            this.floor = new OceanFloor(this.bounds);
            this.surface = new WaterSurface(this.bounds);
            this.rocks = new FloorRocks();
            this.seaweed = new Seaweed();
            this.plankton = new Plankton(this.bounds, 8000);

            // Setup boids
            this.fishTexture = this.resources.items.blueTangTexture;
            this.fishTexture2 = this.resources.items.butterflyFishTexture;
            // filters
            this.fishTexture.minFilter = THREE.LinearFilter;
            this.fishTexture.magFilter = THREE.LinearFilter;
            this.fishTexture.generateMipmaps = false;
            this.fishTexture.needsUpdate = true;
            this.fishTexture2.minFilter = THREE.LinearFilter;
            this.fishTexture2.magFilter = THREE.LinearFilter;
            this.fishTexture2.generateMipmaps = false;
            this.fishTexture2.needsUpdate = true;

            this.boids = new BoidSimulation(
                16,
                0xffffff,
                this.bounds,
                this.resources.items.blueTangModel,
                this.fishTexture,
            );
            this.boids2 = new BoidSimulation(
                16,
                0xffffff,
                this.bounds,
                this.resources.items.butterflyFishModel,
                this.fishTexture2,
            );

            // Add to scene
            this.scene.add(
                this.floor.mesh,
                this.surface.mesh,
                this.rocks.model,
                this.seaweed.mesh,
                this.lights.sunLight,
                this.lights.ambientLight,
                this.plankton.mesh,
                this.boids.mesh,
                this.boids2.mesh,
            );

            // Apply caustics uniforms only to selected meshes for optimization
            const causticMeshes = [
                this.surface.mesh,
                this.floor.mesh,
                this.rocks.mesh,
                this.seaweed.mesh,
                this.boids.mesh,
                this.boids2.mesh,
            ];

            causticMeshes.forEach((mesh) => {
                if (mesh.material) {
                    this.caustics.applyCaustics(mesh.material);
                }
            });
        });
    }

    update() {
        if (this.caustics) {
            this.caustics.update();
        }
        if (this.seaweed) {
            this.seaweed.update();
        }
        if (this.floor) {
            this.floor.update();
        }
        if (this.plankton) {
            this.plankton.update();
        }
        if (this.boids) {
            this.boids.update(this.experience.time.delta);
        }
        if (this.boids2) {
            this.boids2.update(this.experience.time.delta);
        }
    }
}
